import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAppContext } from '../../../../../app/Context';
import { ClassListItem } from '../../../../../db/classes/types';
import { CourseListItem } from '../../../../../db/courses/types';
import { PollSlideTypes } from '../../../../../db/types';
import { i18nNS } from '../../../../../i18n';
import routes from '../../../../../routes/router';
import { FormValues as IntroductionFormValues } from '../../../../shared/TileIntroductionForm/types';
import { setPieChartSkipIntroduction } from '../../../../../db/polls';
import { SlideType } from '../../../../../db/shared/types';
import { setActivityType } from '../../../../../db/shared';
import { copyActivity } from '../../../../../db/class-activities';
import { ClassActivityPluralTypes } from '../../../../../db/class-activities/types';
import { SelectedPoll } from '../../PollsForm';
import { PollTypes } from '../../../../../db/polls/types';

export interface UsePieChartSteps {
  goToNextStep: () => void;
}

const usePieChartSteps = ({ goToNextStep }: UsePieChartSteps) => {
  const { t } = useTranslation([i18nNS.POLL, i18nNS.COMMON]);
  const { presentationId, activeSlideId, session, setSlideTitleAndSubtitle } = useAppContext();

  const navigate = useNavigate();

  const [selectedCourse, setSelectedCourse] = useState<CourseListItem | null>(null);
  const [selectedClass, setSelectedClass] = useState<ClassListItem | null>(null);

  const handleClickIntroductionNext = (data: IntroductionFormValues) => {
    setPieChartSkipIntroduction(data.doNotShowAgain);
    goToNextStep();
  };

  const handleClickCourseSelectionNext = (data: { selectedCourse: CourseListItem }) => {
    setSelectedCourse(data.selectedCourse);
    goToNextStep();
  };

  const handleClickClassSelectionNext = (data: { selectedClass: ClassListItem }) => {
    setSelectedClass(data.selectedClass);
    goToNextStep();
  };

  const handleClickPollSelectionNext = async (data: { selectedPoll: SelectedPoll }) => {
    if (!session?.university) return;
    if (!selectedCourse?.courseId) return;
    if (!selectedClass?.classId) return;
    if (!data.selectedPoll) return;
    if (!presentationId) return;
    if (!activeSlideId) return;

    let pollUrl = '';

    if (data.selectedPoll.pollType === PollTypes.UNPUBLISHED) {
      pollUrl = routes.poll.url({
        universitySlug: session.university.slug,
        courseId: selectedCourse.courseId,
        classId: selectedClass.classId,
        pollId: data.selectedPoll.activityId,
        slideType: PollSlideTypes.PIE_CHART,
      });
    } else {
      const response = await copyActivity({
        activityId: data.selectedPoll.activityData.activityId,
        activityType: ClassActivityPluralTypes.POLLS,
        destinationClassId: selectedClass.classId,
        destinationCourseId: selectedCourse.courseId,
        sourceCourseId: data.selectedPoll.courseData.id,
      });

      const { newCourseId, newClassId, newActivityId } = response;

      pollUrl = routes.poll.url({
        universitySlug: session.university.slug,
        courseId: newCourseId,
        classId: newClassId,
        pollId: newActivityId,
        slideType: PollSlideTypes.PIE_CHART,
      });
    }

    setActivityType(presentationId, activeSlideId, SlideType.PIE_CHART, pollUrl);

    setSlideTitleAndSubtitle({
      title: t('acadly_activity_plugin_course_code', {
        ns: i18nNS.COMMON,
        courseCode: selectedCourse.code,
      }),
      subtitle: t('poll_was_not_conducted', { ns: i18nNS.POLL }),
    });

    navigate(pollUrl, { replace: true });
  };

  return {
    selectedCourse,
    selectedClass,
    handleClickIntroductionNext,
    handleClickCourseSelectionNext,
    handleClickClassSelectionNext,
    handleClickPollSelectionNext,
  };
};

export default usePieChartSteps;
